import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Save, Mail, CheckCircle2, Info, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const STATUS_LABELS = {
    reviewing: "Reviewing",
    contacted: "Contacted",
    hired: "Hired",
    rejected: "Rejected",
};
const STATUS_HELP = {
    reviewing: "Sent when you move an applicant from New → Reviewing.",
    contacted: "Sent when you invite an applicant to interview / next round.",
    hired: "Sent when you mark an applicant Hired.",
    rejected: "Sent when you mark an applicant Rejected — auto-decline copy.",
};

const EmployerTemplates = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/employer/templates");
            setTemplates(data.templates || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const patch = (status, key, value) => {
        setTemplates((rows) => rows.map((t) => (t.status === status ? { ...t, [key]: value } : t)));
    };

    const save = async (t) => {
        setBusy(t.status);
        try {
            await api.put(`/employer/templates/${t.status}`, {
                status: t.status, subject: t.subject, body: t.body, enabled: t.enabled,
            });
            toast.success(`${STATUS_LABELS[t.status]} template saved.`);
            patch(t.status, "is_default", false);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(null); }
    };

    if (loading) return <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>;

    return (
        <div data-testid="employer-templates-page" className="space-y-6">
            <div>
                <p className="overline">Employer · Automation</p>
                <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Auto-Reply Templates
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    When you flip an application into a status, Simonara sends the applicant your template. Turn only the ones you want to be automatic.
                </p>
                <div className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground bg-muted/60 px-3 py-1.5 rounded-full">
                    <Info size={12} /> Placeholders you can use: <code className="text-foreground">{`{name}`}</code>, <code className="text-foreground">{`{job_title}`}</code>, <code className="text-foreground">{`{company}`}</code>
                </div>
            </div>

            <div className="space-y-4">
                {templates.map((t) => (
                    <div key={t.status} data-testid={`template-card-${t.status}`} className="card-soft p-5 space-y-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <div className="flex items-center gap-2">
                                    <Mail size={14} className="text-emerald-500" />
                                    <h2 className="font-heading font-semibold text-lg text-foreground">{STATUS_LABELS[t.status]} email</h2>
                                    {t.is_default && <Badge variant="outline" className="text-[10px]">Default copy</Badge>}
                                    {t.enabled && <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-0 text-[10px]"><CheckCircle2 size={9} className="inline mr-0.5" /> Automated</Badge>}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{STATUS_HELP[t.status]}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch
                                    checked={!!t.enabled}
                                    onCheckedChange={(v) => patch(t.status, "enabled", v)}
                                    data-testid={`template-toggle-${t.status}`}
                                />
                                <span className="text-xs text-muted-foreground">{t.enabled ? "On" : "Off"}</span>
                            </div>
                        </div>

                        <div>
                            <Label>Subject</Label>
                            <Input
                                value={t.subject}
                                onChange={(e) => patch(t.status, "subject", e.target.value)}
                                data-testid={`template-subject-${t.status}`}
                                className="mt-1.5"
                                placeholder="e.g., Next steps for your {job_title} application"
                            />
                        </div>
                        <div>
                            <Label>Body</Label>
                            <Textarea
                                value={t.body}
                                onChange={(e) => patch(t.status, "body", e.target.value)}
                                data-testid={`template-body-${t.status}`}
                                rows={7}
                                className="mt-1.5"
                                placeholder="Hi {name},&#10;&#10;We would love to move forward on {job_title}..."
                            />
                        </div>
                        <button
                            onClick={() => save(t)}
                            disabled={busy === t.status}
                            data-testid={`template-save-${t.status}`}
                            className="btn-green text-sm disabled:opacity-60"
                        >
                            {busy === t.status ? <><Loader2 size={13} className="mr-1.5 inline animate-spin" /> Saving…</> : <><Save size={13} className="mr-1.5 inline" /> Save {STATUS_LABELS[t.status]} template</>}
                        </button>
                    </div>
                ))}
            </div>

            <div className="card-soft p-5 bg-muted/40">
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                    <Sparkles size={14} /> <span className="text-sm font-semibold">Why templates matter</span>
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground max-w-2xl">
                    Fast, personalized replies push accept rates 20-30%. Simonara handles the send so your team never ghosts a candidate — even when the pipeline gets busy.
                </p>
            </div>
        </div>
    );
};

export default EmployerTemplates;
